import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import styles from './ConfirmDialog.module.css';

interface ConfirmDialogProps {
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'neutral';
  confirmPhrase?: string;
  confirmPhraseLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'neutral',
  confirmPhrase,
  confirmPhraseLabel,
  onConfirm,
  onCancel,
}) => {
  const [typed, setTyped] = useState('');

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  const ready = !confirmPhrase || typed.trim() === confirmPhrase;

  return (
    <div className={styles.overlay} onClick={onCancel} role="presentation">
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.head}>
          {variant === 'danger' && <AlertTriangle size={18} className={styles.dangerIcon} />}
          <h3 className={styles.title}>{title}</h3>
        </div>

        <div className={styles.message}>{message}</div>

        {confirmPhrase && (
          <>
            <label className={styles.phraseLabel} htmlFor="confirm-dialog-phrase">
              {confirmPhraseLabel ?? `Type "${confirmPhrase}" to confirm`}
            </label>
            <input
              id="confirm-dialog-phrase"
              className={styles.input}
              value={typed}
              autoFocus
              placeholder={confirmPhrase}
              onChange={(event) => setTyped(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && ready) onConfirm();
              }}
            />
          </>
        )}

        <div className={styles.actions}>
          <button type="button" className={styles.cancelBtn} onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`${styles.confirmBtn} ${variant === 'danger' ? styles.danger : ''}`}
            disabled={!ready}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
